import React, { useCallback, useEffect, useState } from 'react';
import { X, Gift, Clock, Pickaxe, Package, Banknote, Coins, CheckCircle2, Loader2, Flame } from 'lucide-react';
import { gameNotice } from './oilNotice';
import { oilApi, fmt } from './oilApi';
import { KakoCoin } from './OilIcons';
import { sfx, playClip, sfxClose } from './oilSounds';

const SFX_MISSION = '/oil/sfx_missao.mp3';

const ICON = { clock: Clock, drill: Pickaxe, barrel: Package, cash: Banknote, coins: Coins };

const compact = (n) => {
  const v = Number(n) || 0;
  if (v >= 1000000) return `${(v / 1000000).toFixed(v % 1000000 ? 1 : 0)}M`;
  if (v >= 1000) return `${(v / 1000).toFixed(v % 1000 ? 1 : 0)}K`;
  return String(v);
};

const MissionRow = ({ m, busy, onClaim }) => {
  const Icon = ICON[m.icon] || Gift;
  const pct = Math.min(100, Math.round((m.progress / m.target) * 100));
  return (
    <div
      data-testid={`oil-mission-${m.id}`}
      onClick={() => playClip(SFX_MISSION)}
      className={`cursor-pointer rounded-xl border p-2.5 transition-colors ${
        m.claimed ? 'border-zinc-800 bg-black/40 opacity-60'
          : m.done ? 'border-amber-500/80 bg-amber-950/40 shadow-[0_0_12px_rgba(255,180,0,0.18)]'
            : 'border-zinc-800 bg-black/50'
      }`}
    >
      <div className="flex items-center gap-2">
        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border ${
          m.done ? 'border-amber-500/70 bg-amber-900/40' : 'border-zinc-700 bg-zinc-900'}`}>
          <Icon className={`h-4 w-4 ${m.done ? 'text-amber-300' : 'text-zinc-400'}`} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs font-black text-zinc-100">{m.title}</p>
          <p className="truncate text-[10px] text-zinc-400">{m.desc}</p>
        </div>
        {m.claimed ? (
          <span className="flex items-center gap-1 text-[10px] font-black text-emerald-500" data-testid={`oil-mission-done-${m.id}`}>
            <CheckCircle2 className="h-3.5 w-3.5" /> RECEBIDO
          </span>
        ) : m.done ? (
          <button
            onClick={() => onClaim(m.id)}
            disabled={busy}
            data-testid={`oil-mission-claim-${m.id}`}
            className="flex shrink-0 items-center gap-1 rounded-lg border border-amber-400 bg-gradient-to-b from-amber-400 to-amber-700 px-2.5 py-1.5 text-[10px] font-black text-black transition-transform active:scale-95 disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <>PEGAR +{m.reward}</>}
          </button>
        ) : (
          <span className="shrink-0 text-[10px] font-black text-amber-500/90">+{m.reward}</span>
        )}
      </div>
      {!m.claimed && (
        <div className="relative mt-2 h-4 w-full overflow-hidden rounded-full bg-zinc-800">
          <div className={`h-full rounded-full transition-[width] duration-500 ${m.done ? 'bg-amber-400' : 'bg-amber-700'}`}
            style={{ width: `${pct}%` }} data-testid={`oil-mission-bar-${m.id}`} />
          <span className="absolute inset-0 flex items-center justify-center whitespace-nowrap px-1 text-[9px] font-black text-amber-50 [text-shadow:0_1px_2px_rgba(0,0,0,0.95)]"
            data-testid={`oil-mission-progress-${m.id}`}>
            {compact(m.progress)}/{compact(m.target)}
          </span>
        </div>
      )}
    </div>
  );
};

export const OilMissions = ({ onClose, refresh }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await oilApi.get('/missions');
      setData(r.data);
    } catch {
      setData({ error: true });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const claim = async (id) => {
    setBusy(id);
    try {
      const r = await oilApi.post(`/missions/${id}/claim`);
      setData(r.data.state);
      sfx.cash();
      gameNotice.ok(`+${fmt(r.data.reward)} Black Coins`, r.data.title);
      refresh?.();
    } catch (e) {
      gameNotice.fail('Recompensa indisponível', e.response?.data?.detail || 'Não foi possível receber agora.');
      load();
    }
    setBusy(null);
  };

  const claimStreak = async () => {
    setBusy('streak');
    try {
      const r = await oilApi.post('/missions/streak/claim');
      setData(r.data.state);
      sfx.cash();
      gameNotice.ok(`+${fmt(r.data.reward)} Black Coins`, `Bônus de ${streak?.target || 15} dias seguidos!`);
      refresh?.();
    } catch (e) {
      gameNotice.fail('Bônus indisponível', e.response?.data?.detail || 'Tenta de novo mais tarde.');
      load();
    }
    setBusy(null);
  };

  const streak = data?.streak;

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/80 backdrop-blur-sm sm:items-center" data-testid="oil-missions-modal">
      <div className="max-h-[92vh] w-full max-w-md overflow-y-auto rounded-t-2xl border border-amber-800/70 bg-[#12100a] p-3.5 shadow-2xl sm:rounded-2xl">
        <div className="mb-3 flex items-start justify-between gap-2">
          <div>
            <p className="flex items-center gap-1.5 text-base font-black tracking-wide text-amber-300">
              <Gift className="h-4 w-4" /> MISSÕES DIÁRIAS
            </p>
            <p className="text-[10px] text-zinc-400">
              Zera à meia-noite · até {fmt(data?.daily_total || 450)} Black Coins por dia
            </p>
          </div>
          <button onClick={() => { sfxClose(); onClose(); }} data-testid="oil-missions-close"
            className="rounded-lg border border-zinc-700 bg-black/60 p-1.5 text-zinc-400 transition-transform active:scale-95">
            <X className="h-4 w-4" />
          </button>
        </div>

        {!data && (
          <div className="flex items-center justify-center gap-2 py-10 text-xs text-zinc-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Carregando missões...
          </div>
        )}

        {data?.error && (
          <p className="py-10 text-center text-xs text-red-400">Não foi possível carregar as missões.</p>
        )}

        {data?.missions && (
          <>
            {/* sequência de dias */}
            <div className="mb-3 rounded-xl border border-orange-800/70 bg-gradient-to-b from-orange-950/60 to-black/60 p-2.5" data-testid="oil-streak-box">
              <div className="flex items-center justify-between gap-2">
                <p className="flex items-center gap-1.5 text-[11px] font-black text-orange-300">
                  <Flame className="h-3.5 w-3.5" /> {streak.days} dia{streak.days === 1 ? '' : 's'} seguido{streak.days === 1 ? '' : 's'}
                </p>
                {streak.claimable ? (
                  <button onClick={() => { playClip(SFX_MISSION); claimStreak(); }} disabled={busy === 'streak'} data-testid="oil-streak-claim"
                    className="flex items-center gap-1 rounded-lg border border-orange-300 bg-gradient-to-b from-orange-400 to-orange-700 px-2.5 py-1.5 text-[10px] font-black text-black transition-transform active:scale-95 disabled:opacity-50">
                    {busy === 'streak' ? <Loader2 className="h-3 w-3 animate-spin" /> : <>PEGAR BÔNUS +{streak.reward}</>}
                  </button>
                ) : (
                  <span className="text-[10px] font-bold text-orange-200/80">
                    {streak.target} dias → +{fmt(streak.reward)} <KakoCoin />
                  </span>
                )}
              </div>
              <div className="mt-2 flex gap-1">
                {Array.from({ length: streak.target }).map((_, i) => (
                  <span key={i} className={`h-1.5 flex-1 rounded-full ${i < streak.days ? 'bg-orange-400' : 'bg-zinc-800'}`} />
                ))}
              </div>
            </div>

            <div className="space-y-2">
              {data.missions.map((m) => (
                <MissionRow key={m.id} m={m} busy={busy === m.id} onClaim={claim} />
              ))}
            </div>

            <p className="mt-3 text-center text-[10px] text-zinc-500" data-testid="oil-missions-earned">
              Recebido hoje: <b className="text-amber-400">{fmt(data.earned_today)}</b> de {fmt(data.daily_total)} Black Coins
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default OilMissions;
